// src/context/UserContext.js
import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";
import { apiRequest } from "../utils/api";

const UserContext = createContext(null);

// Helper para comparar si los datos han cambiado
const hasDataChanged = (oldData, newData) => {
  if (!oldData && newData) return true;
  if (oldData && !newData) return true;
  if (!oldData && !newData) return false;
  
  return JSON.stringify(oldData) !== JSON.stringify(newData);
};

export function UserProvider({ children }) {
  const { user, loading: authLoading, getToken } = useAuth();
  const [profesionalData, setProfesionalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const fetchingRef = useRef(false);

  // Función para obtener los datos del profesional
  const fetchProfesionalData = useCallback(async (force = false) => {
    // Evitar múltiples llamadas simultáneas
    if (fetchingRef.current && !force) {
      return profesionalData;
    }

    // Si ya tenemos datos y no forzamos la actualización, no hacer nada
    if (profesionalData && !force && lastFetched) {
      const timeSinceLastFetch = Date.now() - lastFetched;
      // No refrescar si han pasado menos de 30 segundos
      if (timeSinceLastFetch < 30000) {
        return profesionalData;
      }
    }

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      if (!token) {
        throw new Error("No hay token de autenticación");
      }

      const response = await apiRequest('/datos-profesional', {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Error al obtener datos: ${response.status}`);
      }

      const data = await response.json();

      // Solo actualizar si los datos han cambiado
      if (hasDataChanged(profesionalData, data)) {
        setProfesionalData(data);
      }

      setLastFetched(Date.now());
      return data;
    } catch (err) {
      console.error("Error al obtener datos del profesional:", err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [getToken, profesionalData, lastFetched]);

  // Función para actualizar datos específicos localmente (sin hacer petición)
  const updateLocalData = useCallback((updates) => {
    setProfesionalData(prev => {
      if (!prev) return updates;
      const newData = { ...prev, ...updates };
      // Solo actualizar si realmente hay cambios
      if (hasDataChanged(prev, newData)) {
        return newData;
      }
      return prev;
    });
  }, []);

  // Función para refrescar los datos forzadamente
  const refreshData = useCallback(() => {
    return fetchProfesionalData(true);
  }, [fetchProfesionalData]);

  // Función para limpiar los datos (logout)
  const clearData = useCallback(() => {
    setProfesionalData(null);
    setLastFetched(null);
    setError(null);
  }, []);

  // Cargar datos cuando el usuario se autentica
  useEffect(() => {
    if (!authLoading && user && !profesionalData && !fetchingRef.current) {
      fetchProfesionalData();
    }
    
    // Limpiar datos cuando el usuario hace logout
    if (!authLoading && !user && profesionalData) {
      clearData();
    }
  }, [user, authLoading, profesionalData, fetchProfesionalData, clearData]);

  // Datos extraídos para fácil acceso
  const value = {
    // Datos completos del profesional
    profesionalData,
    
    // Datos individuales para acceso rápido
    nombre: profesionalData?.nombre || "",
    apellido: profesionalData?.apellido || "",
    email: profesionalData?.email || "",
    celular: profesionalData?.celular || "",
    imagen: profesionalData?.imagen || "",
    genero: profesionalData?.genero || "",
    descripcion: profesionalData?.descripcion || "",
    profesion: profesionalData?.profesion || "",
    labels: profesionalData?.labels || [],
    especialidades: profesionalData?.labels || [],
    honorarios: profesionalData?.honorarios || 0,
    banco: profesionalData?.banco || "",
    numeroCuenta: profesionalData?.numeroCuenta || "",
    numeroSucursal: profesionalData?.numeroSucursal || "",
    documento: profesionalData?.documento || "",
    profesionalId: profesionalData?.profesionalId || profesionalData?.id || null,
    
    // Estados
    loading,
    error,
    lastFetched,
    isDataLoaded: !!profesionalData,
    
    // Funciones
    fetchProfesionalData,
    refreshData,
    updateLocalData,
    clearData,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser debe ser usado dentro de un UserProvider");
  }
  return context;
}

// Hook para obtener datos con carga automática
export function useProfesionalData() {
  const { profesionalData, loading, error, fetchProfesionalData, isDataLoaded } = useUser();
  
  useEffect(() => {
    if (!isDataLoaded && !loading) {
      fetchProfesionalData();
    }
  }, [isDataLoaded, loading, fetchProfesionalData]);
  
  return { data: profesionalData, loading, error };
}

export default UserContext;

